// Scansione AI della bolla fornitore: foto multiple -> righe riconosciute -> crea carico
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ScanText, Trash2 } from "lucide-react";
import { wmsApi } from "./wmsApi";
import { WButton, WInput, WField, WBadge, WModal } from "./WmsUi";
import WmsCamera from "./WmsCamera";

export default function WmsScanDocument({ open, onClose }) {
  const navigate = useNavigate();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [boot, setBoot] = useState(null);
  const [creating, setCreating] = useState(false);
  const [searchIdx, setSearchIdx] = useState(null);
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    if (open) { setResult(null); setCameraOpen(true); }
  }, [open]);
  useEffect(() => { if (open && !boot) wmsApi.get("/wms/bootstrap").then((r) => setBoot(r.data)).catch(() => {}); }, [open, boot]);

  const analyze = async (blobs) => {
    setAnalyzing(true);
    try {
      const fd = new FormData();
      blobs.forEach((b, i) => fd.append("files", b, `doc_${i}.jpg`));
      const r = await wmsApi.post("/wms/ai/scan-document", fd);
      if (!r.data.success) { toast.error(r.data.error || "Documento illeggibile"); onClose(); return; }
      setResult({
        ...r.data,
        rows: r.data.rows.map((row) => ({ ...row, product_id: row.matches?.[0]?.product_id || "", skip: false })),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore analisi AI");
      onClose();
    } finally { setAnalyzing(false); }
  };

  const setRow = (i, patch) => setResult((r) => ({ ...r, rows: r.rows.map((row, j) => (j === i ? { ...row, ...patch } : row)) }));

  const searchResults = useMemo(() => {
    if (!boot || searchQ.length < 2) return [];
    const s = searchQ.toLowerCase();
    return boot.products.filter((p) => `${p.code} ${p.name} ${p.category_name}`.toLowerCase().includes(s)).slice(0, 6);
  }, [boot, searchQ]);

  const prodById = (id) => boot?.products.find((p) => p.id === id);

  const create = async () => {
    const rows = result.rows.filter((r) => !r.skip);
    if (rows.some((r) => !r.product_id)) { toast.error("Abbina tutti gli articoli (o ignora le righe)"); return; }
    if (!result.supplier_name?.trim()) { toast.error("Inserisci il fornitore"); return; }
    setCreating(true);
    try {
      const rec = await wmsApi.post("/wms/receipts", {
        supplier_name: result.supplier_name, ddt_number: result.ddt_number || "", ddt_date: result.ddt_date || "",
      });
      for (const row of rows) {
        await wmsApi.post(`/wms/receipts/${rec.data.id}/lots`, {
          product_id: row.product_id,
          supplier_description: row.supplier_description || "",
          supplier_product_code: row.supplier_code || "",
          packaging: row.pieces && !row.quantity_kg ? "cartone" : "appeso",
          um: row.quantity_kg ? "kg" : "pz",
          lot_supplier: row.lot || "",
          purchase_price: row.price ?? null,
          price_um: "kg",
          expected_kg: row.quantity_kg ?? null,
          expected_units: row.pieces ?? null,
        });
      }
      toast.success(`Carico ${rec.data.number} creato con ${rows.length} righe dalla bolla`);
      onClose();
      navigate(`/wms/carichi/${rec.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione carico");
    } finally { setCreating(false); }
  };

  if (!open) return null;
  return (
    <>
      <WmsCamera open={cameraOpen} onClose={() => { setCameraOpen(false); if (!result && !analyzing) onClose(); }} onDone={analyze} multi title="Scansiona bolla fornitore (più pagine ok)" />

      {analyzing && (
        <div className="fixed inset-0 z-[55] bg-black/80 flex flex-col items-center justify-center gap-4" data-testid="scan-analyzing">
          <ScanText className="w-14 h-14 text-[#FF4500] animate-pulse" />
          <p className="text-white font-['Chivo'] font-black text-xl uppercase">L'AI sta leggendo il documento…</p>
        </div>
      )}

      <WModal open={!!result} onClose={onClose} title="Bolla riconosciuta — verifica e crea il carico" wide>
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <WField label="Fornitore"><WInput value={result.supplier_name} onChange={(e) => setResult({ ...result, supplier_name: e.target.value })} data-testid="scandoc-supplier" /></WField>
              <WField label="N. DDT"><WInput value={result.ddt_number} onChange={(e) => setResult({ ...result, ddt_number: e.target.value })} data-testid="scandoc-ddt" /></WField>
              <WField label="Data"><WInput type="date" value={result.ddt_date} onChange={(e) => setResult({ ...result, ddt_date: e.target.value })} data-testid="scandoc-date" /></WField>
            </div>

            <div className="space-y-3">
              {result.rows.map((row, i) => (
                <div key={i} className={`border-2 p-3 bg-white ${row.skip ? "opacity-40 border-zinc-200" : "border-zinc-300"}`} data-testid={`scandoc-row-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold text-zinc-400">Dicitura fornitore (resta associata al fornitore)</p>
                      <WInput value={row.supplier_description} onChange={(e) => setRow(i, { supplier_description: e.target.value })} className="!h-11 !text-sm font-bold" data-testid={`scandoc-desc-${i}`} />
                    </div>
                    <button onClick={() => setRow(i, { skip: !row.skip })} className="h-11 px-3 border-2 border-zinc-300 text-zinc-500 font-bold text-xs uppercase hover:border-red-500 hover:text-red-600 shrink-0" data-testid={`scandoc-skip-${i}`}>
                      {row.skip ? "Ripristina" : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm font-semibold text-zinc-600">
                    {row.quantity_kg != null && <span>{row.quantity_kg} kg</span>}
                    {row.pieces != null && <span>{row.pieces} colli/pz</span>}
                    {row.lot && <span>Lotto: {row.lot}</span>}
                    {row.price != null && <span>{row.price} €</span>}
                  </div>
                  {!row.skip && (
                    <div className="mt-2">
                      <p className="text-[10px] uppercase font-bold text-zinc-400 mb-1">Nostro articolo</p>
                      {row.product_id ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <WBadge tone="green">{prodById(row.product_id)?.name || "?"}</WBadge>
                          <WBadge tone="dark">{prodById(row.product_id)?.category_name || "—"}</WBadge>
                          <button className="text-xs font-bold uppercase text-red-600" onClick={() => { setRow(i, { product_id: "" }); setSearchIdx(i); setSearchQ(""); }} data-testid={`scandoc-change-${i}`}>Cambia</button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-1.5">
                            {(row.matches || []).map((m) => (
                              <button key={m.product_id} onClick={() => setRow(i, { product_id: m.product_id })} className="h-10 px-3 border-2 border-zinc-300 text-xs font-bold hover:border-zinc-900" data-testid={`scandoc-match-${i}-${m.product_id}`}>
                                {m.name} <span className="text-zinc-400">· {m.category_name || "—"}</span>
                              </button>
                            ))}
                          </div>
                          <WInput placeholder="Cerca articolo..." value={searchIdx === i ? searchQ : ""} onFocus={() => setSearchIdx(i)} onChange={(e) => { setSearchIdx(i); setSearchQ(e.target.value); }} className="!h-11 !text-sm" data-testid={`scandoc-search-${i}`} />
                          {searchIdx === i && searchResults.map((p) => (
                            <button key={p.id} onClick={() => { setRow(i, { product_id: p.id }); setSearchQ(""); }} className="w-full text-left px-3 h-10 bg-white border border-zinc-200 hover:bg-orange-50 text-sm font-semibold flex items-center gap-2" data-testid={`scandoc-opt-${i}-${p.code}`}>
                              <span className="font-mono text-xs text-zinc-400">{p.code}</span> {p.name} <WBadge tone="dark">{p.category_name || "—"}</WBadge>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <WButton variant="primary" className="w-full" onClick={create} disabled={creating} data-testid="scandoc-create-btn">
              {creating ? "Creazione..." : `Crea carico con ${result.rows.filter((r) => !r.skip).length} righe`}
            </WButton>
          </div>
        )}
      </WModal>
    </>
  );
}
